import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { getCardData } from "../cards/collectionProvider/CollectionProvider";
import { isChallengeReviewCurrent } from "../cards/components/ChallengeReviewedBadge";
import { useDismissible } from "../native/dismissibleStack";
import {
  createCardChallenge,
  submitChallengeEvidence,
  uploadChallengeEvidence,
} from "./verificationApi";
import { deviceDeadlineMs, isExpiredChallenge } from "./challengeExpiry";
import { photoRejection } from "../utils/photoLimits";
import { claimRefusals } from "../utils/refusalNotice";

const initialState = {
  step: "idle",
  challenge: null,
  file: null,
  // The evidence object already in the bucket for `file`, if the upload
  // succeeded on an earlier Submit whose second call then failed. Submitting is
  // two unrelated writes -- upload, then point the challenge at the key -- and a
  // failure between them leaves an object nothing references. Re-uploading on
  // retry adds another one per press, and a 409 leaves Submit live, so an
  // expired challenge can be pressed indefinitely. Keeping the key makes the
  // retry reuse the object it already paid for.
  //
  // It MUST belong to the CURRENT `file`, or the retry submits the old photo
  // while the UI names the new one -- wrong evidence in the review queue, which
  // is worse than the leak. Two things enforce that, and it needs both: every
  // write of `file` below clears it, AND the store after the upload re-checks
  // that `file` has not changed underneath it. Clearing alone is not enough --
  // the store runs after an `await` against state captured at render time, so a
  // file swapped in mid-upload would be overwritten by the resolving upload.
  // That interleaving is not reachable through the UI today (Choose and Submit
  // both carry `disabled={busy}`, and the hidden input's only trigger is that
  // disabled button) -- it is guarded here because the invariant is what makes
  // reuse safe, and it should not rest on a `disabled` prop in a sibling.
  uploadedKey: null,
  error: "",
};

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return "";
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
};

const getErrorMessage = (err) => {
  if (err?.status === 409) {
    return "This challenge expired or was already submitted. Close and start a new challenge.";
  }
  return err?.message || "Something went wrong. Please try again.";
};

// Coarse on purpose. The only thing this has to move is a 15-minute boundary
// (CARD_VERIFICATION_CHALLENGE_TTL_MINUTES, server/verification/verificationController.js),
// so one second is already far finer than the deadline it watches, and it costs
// one state bump per second only while an unexpired challenge is on screen in an
// open modal.
const EXPIRY_TICK_MS = 1000;

const CardChallengeModal = ({ open, onClose, userCard, currentChallenge, onChallengeUpdated }) => {
  const [{ step, challenge, file, uploadedKey, error }, setState] = useState(initialState);
  // Never read. Bumping it is the whole mechanism: every expiry predicate below
  // reads Date.now() at render time, so a bare re-render is what notices the
  // deadline passing.
  const [, setExpiryTick] = useState(0);
  const fileInputRef = useRef(null);
  const card = useMemo(() => getCardData(userCard), [userCard]);
  const activeChallenge = challenge || currentChallenge;
  // #260 -- the same device-clock instant the predicate above tests against, so
  // the tick cannot start, stop or retire on a different boundary from the one
  // that decides which panel renders.
  const deadlineMs = deviceDeadlineMs(activeChallenge);

  // Without this the modal only re-evaluates expiry when something else makes it
  // render -- and the 15 minutes exist precisely so the user can walk away and
  // photograph the card, touching nothing. Runs only while the modal is open AND
  // the deadline is still ahead; stops itself the moment it is behind, and the
  // cleanup covers unmount and close.
  useEffect(() => {
    if (!open) return undefined;
    if (!Number.isFinite(deadlineMs) || deadlineMs <= Date.now()) return undefined;
    const id = setInterval(() => {
      setExpiryTick((t) => t + 1);
      if (Date.now() >= deadlineMs) clearInterval(id);
    }, EXPIRY_TICK_MS);
    return () => clearInterval(id);
  }, [open, deadlineMs]);

  const busy = step === "creating" || step === "uploading" || step === "submitting";
  // #16 -- this test used to be spelled inline on the idle branch only, so once
  // `step` was literally "ready" nothing re-evaluated it: the user came back from
  // photographing the card and Submit was still armed on a challenge the server
  // hard-rejects (`challenge.expiresAt <= new Date()`,
  // server/verification/verificationController.js). The submit failure path sets
  // `step` back to "ready", which re-armed it again on the same dead code.
  // Hoisted out of the branch so it holds wherever the challenge is live.
  const challengeExpired = !!activeChallenge && isExpiredChallenge(activeChallenge);
  const ready =
    !challengeExpired &&
    (step === "ready" || (step === "idle" && activeChallenge?.status === "pending"));
  const submitted = step === "submitted" || (step === "idle" && activeChallenge?.status === "submitted");
  const approved = step === "idle" && activeChallenge?.status === "approved";
  const rejected = step === "idle" && activeChallenge?.status === "rejected";
  // The badge and the Challenge row drift apart by design. UserCard's pre-save
  // hook `clearChallengeBadgeWhenCardChanges` nulls `challengeReviewedAt` and
  // `challengeId` whenever `userId`, `photos` or `collectionType` is modified,
  // while the Challenge document keeps `status: "approved"` -- nothing writes to
  // it. So `approved` above can be true for an owner whose badge is no longer
  // published, and that owner arrives here from CollectionPage's "Run Challenge
  // Again", which is gated on this same predicate (`needsRechallenge`). Without a
  // start button the branch is a dead end for exactly the people who came here to
  // act. The badge also ages out on its own at CHALLENGE_BADGE_TTL_DAYS, which
  // this covers too.
  const badgeStale = !isChallengeReviewCurrent(userCard?.challengeReviewedAt);
  // "ready" is the branch this fix is about; "idle" is where the check already
  // lived. The other steps are deliberately excluded so the panel cannot render
  // on top of one that owns the screen: "creating" is a spinner for the
  // replacement challenge, "uploading"/"submitting" are a request in flight that
  // must be allowed to land (its failure returns to "ready", where this fires),
  // "submitted"/"error" are terminal panels of their own.
  const expired = challengeExpired && (step === "idle" || step === "ready");

  const resetAndClose = () => {
    setState(initialState);
    onClose();
  };

  // Registered even while busy, deliberately: an upload in flight should swallow
  // Back rather than let it fall through and close the app mid-submission. This
  // matches the Dialog's own `onClose={busy ? undefined : resetAndClose}`.
  useDismissible(
    open,
    () => {
      if (!busy) resetAndClose();
    },
    "card-challenge-modal"
  );

  const patchState = (patch) =>
    setState((prev) => ({ ...prev, ...patch }));

  const handleStart = async () => {
    if (!userCard?._id) return;
    // A new challenge mints a new `challengeCode`, and the evidence photo has to
    // show it. Any object uploaded for the previous challenge therefore shows
    // the WRONG code and must not be reused -- this reverts the retry to a fresh
    // upload, exactly as before.
    //
    // `file` goes for the identical reason (#16): the chosen photo shows the old
    // code too. It matters now because expiring out of "ready" reaches a Start
    // button with a photo already selected -- keep it and the modal would say
    // "Selected: card.jpg" over a fresh code and submit a photo the reviewer must
    // reject.
    patchState({ step: "creating", file: null, uploadedKey: null, error: "" });
    try {
      const created = await createCardChallenge(userCard._id);
      patchState({ step: "ready", challenge: created, uploadedKey: null, error: "" });
      if (onChallengeUpdated) onChallengeUpdated(created);
    } catch (err) {
      // Both steps show the failure inside this dialog (B-31).
      claimRefusals();
      patchState({ step: "error", error: getErrorMessage(err) });
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length > 1) {
      patchState({ file: null, uploadedKey: null, error: "Please select one image only." });
      return;
    }
    const selected = files[0];
    if (!selected) return;
    // [#24] Refuse what the server would refuse, before uploading it. See
    // `utils/photoLimits.js` -- the message is the server's own string.
    const rejection = photoRejection(selected);
    if (rejection) {
      patchState({ file: null, uploadedKey: null, error: rejection });
      return;
    }
    patchState({ file: selected, uploadedKey: null, error: "" });
  };

  const handleSubmit = async () => {
    if (!activeChallenge?._id || !file) return;
    // #259 -- #16's guard is RENDER-derived: `challengeExpired` (:113) reads
    // Date.now() while rendering, so it cannot disarm a control on a screen that
    // has not re-rendered. The only thing that makes this modal re-render on its
    // own is the one-second interval above, and Android SUSPENDS a backgrounded
    // WebView's timers -- the deadline passes, no tick fires, and the
    // still-mounted Submit is still pressable. (Suspension, not throttling: the
    // usual background clamp is ~1000ms and EXPIRY_TICK_MS is already 1000, so a
    // clamp alone would open no window at all. Inferred from WebView behaviour
    // and NOT yet measured on the tablet.) Re-reading the clock HERE is the only
    // check that runs at press time.
    //
    // Deliberately NOT `disabled={... || challengeExpired}` on the button, which
    // is what #259 recommended: that expression is evaluated during a render, and
    // it is inert in every render that can reach it. Note the button is NOT gated
    // on `ready` alone -- its block at :326 is `(ready || step === "uploading" ||
    // step === "submitting")`, so it CAN be mounted while `challengeExpired` is
    // true. What makes the extra term dead is `busy`: `ready` implies
    // `!challengeExpired` (:115), and the other two arms both imply `busy` (:105),
    // which already forces `disabled`. So the prop can never differ.
    //
    // Bumping the interval's own tick is what renders the expired panel. Falling
    // through instead would upload the photo first -- a real object in the PRIVATE
    // evidence bucket (SUPABASE_EVIDENCE_BUCKET; server/upload/storageProvider.js
    // never calls getPublicUrl for it), still charged against the owner's 200 MB
    // UPLOAD_QUOTA_BYTES -- only for the server to 409 it.
    if (isExpiredChallenge(activeChallenge)) {
      setExpiryTick((t) => t + 1);
      return;
    }
    patchState({ step: "uploading", error: "" });
    try {
      // Only upload when this exact `file` has no object yet. The server binds a
      // key to no SESSION at all -- no nonce, no TTL, no tie to one submit
      // attempt (verification/verificationController.js checks format, owner
      // prefix, and since [#303] that the key names a row in the `evidence`
      // bucket) -- so a key from a failed submit is exactly as acceptable as a
      // fresh one: its UploadedImage row outlives the failure.
      let storageKey = uploadedKey;
      if (!storageKey) {
        const uploaded = await uploadChallengeEvidence(file);
        if (!uploaded?.storageKey) {
          throw new Error("Evidence upload did not return a storage key.");
        }
        storageKey = uploaded.storageKey;
        // Only bank it if this is still the photo we uploaded. `file` here is
        // the render-time capture; `prev.file` is the truth at write time.
        setState((prev) =>
          prev.file === file ? { ...prev, uploadedKey: storageKey } : prev
        );
      }
      patchState({ step: "submitting" });
      const submittedChallenge = await submitChallengeEvidence(activeChallenge._id, storageKey);
      if (onChallengeUpdated) onChallengeUpdated(submittedChallenge);
      patchState({ step: "submitted", uploadedKey: null, error: "" });
    } catch (err) {
      claimRefusals();
      patchState({ step: "ready", error: getErrorMessage(err) });
    }
  };

  return (
    <Dialog
      // Named by its own title (E2E audit 7·17 / 8·1): without a DialogTitle,
      // MUI's aria-labelledby points at nothing and a screen reader says "dialog".
      aria-labelledby="photo-challenge-title"
      open={open}
      onClose={busy ? undefined : resetAndClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ p: 3, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography id="photo-challenge-title" sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
              Photo Challenge
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
              {card?.name || "Collection card"}
              {card?.set?.name ? ` · ${card.set.name}` : ""}
            </Typography>
          </Box>
          <IconButton
            onClick={resetAndClose}
            disabled={busy}
            size="small"
            sx={{ bgcolor: "#f1f5f9", color: "#64748b" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Alert severity="warning" sx={{ borderRadius: 3, mb: 2 }}>
          This does not prove authenticity or guarantee condition.
        </Alert>

        {step === "idle" && !activeChallenge && (
          <Box>
            <Typography sx={{ color: "#334155", fontSize: 14, lineHeight: 1.7 }}>
              Start a private photo challenge for this card. You will receive a
              short code, then upload one photo showing the card and the code
              together.
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mt: 1.5 }}>
              Do not upload ID documents or sensitive personal information.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStart}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 3,
                bgcolor: "#6b21a8",
                fontWeight: 900,
                textTransform: "none",
              }}
            >
              Start Photo Challenge
            </Button>
          </Box>
        )}

        {step === "creating" && (
          <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={32} />
            <Typography sx={{ color: "#64748b", fontWeight: 700 }}>
              Creating photo challenge...
            </Typography>
          </Box>
        )}

        {(ready || step === "uploading" || step === "submitting") && (
          <Box>
            <Box
              sx={{
                border: "1.5px solid rgba(107,33,168,0.18)",
                bgcolor: "rgba(107,33,168,0.06)",
                borderRadius: 3,
                p: 2,
                mb: 2,
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 800, letterSpacing: "0.12em" }}>
                CHALLENGE CODE
              </Typography>
              <Typography sx={{ fontSize: 34, fontWeight: 950, color: "#6b21a8", letterSpacing: "0.12em" }}>
                {activeChallenge?.challengeCode}
              </Typography>
              {activeChallenge?.expiresAt && (
                <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                  Expires {formatExpiry(activeChallenge.expiresAt)}
                </Typography>
              )}
            </Box>

            <Typography sx={{ color: "#334155", fontSize: 14, lineHeight: 1.7, mb: 1.5 }}>
              Take a photo of this card with the code shown below.
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>
              This helps reviewers check that your photo follows the challenge requirements.
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>
              Do not upload ID documents or sensitive personal information.
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleFileSelect}
            />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderRadius: 3,
                py: 1.2,
                borderColor: "rgba(107,33,168,0.25)",
                color: "#6b21a8",
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              {file ? "Choose a different image" : "Choose one image"}
            </Button>

            {file && (
              <Typography sx={{ mt: 1, fontSize: 12, color: "#475569", fontWeight: 700 }}>
                Selected: {file.name}
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={busy || !file}
              startIcon={busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : null}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 3,
                bgcolor: "#6b21a8",
                fontWeight: 900,
                textTransform: "none",
              }}
            >
              {step === "uploading"
                ? "Uploading evidence..."
                : step === "submitting"
                ? "Submitting evidence..."
                : "Submit challenge evidence"}
            </Button>
          </Box>
        )}

        {submitted && (
          <Box>
            <Alert severity="success" sx={{ borderRadius: 3, mb: 2 }}>
              {step === "submitted" ? "Challenge evidence submitted for review." : "Submitted for review"}
            </Alert>
            <Typography sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
              This helps reviewers check that your photo follows the challenge requirements.
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mt: 1 }}>
              This does not prove authenticity or guarantee condition.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={resetAndClose}
              sx={{ mt: 3, py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }}
            >
              Done
            </Button>
          </Box>
        )}

        {approved && (
          <Box>
            <Alert severity="success" sx={{ borderRadius: 3, mb: 2 }}>
              Challenge evidence reviewed
            </Alert>
            <Typography sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
              This does not prove authenticity or guarantee condition.
            </Typography>
            {activeChallenge?.reviewedAt && (
              <Typography sx={{ color: "#64748b", fontSize: 12, mt: 1 }}>
                Reviewed {formatExpiry(activeChallenge.reviewedAt)}
              </Typography>
            )}
            {badgeStale && (
              <Button
                fullWidth
                variant="contained"
                onClick={handleStart}
                sx={{ mt: 3, py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }}
              >
                Start a new photo challenge
              </Button>
            )}
            <Button
              fullWidth
              variant={badgeStale ? "outlined" : "contained"}
              onClick={resetAndClose}
              sx={
                badgeStale
                  ? {
                      mt: 1.5,
                      py: 1.5,
                      borderRadius: 3,
                      color: "#6b21a8",
                      borderColor: "#6b21a8",
                      fontWeight: 900,
                    }
                  : { mt: 3, py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }
              }
            >
              Done
            </Button>
          </Box>
        )}

        {rejected && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>
              Challenge evidence was not accepted for review compliance.
            </Alert>
            <Typography sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
              This does not prove authenticity or guarantee condition.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStart}
              sx={{ mt: 3, py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }}
            >
              Start a new photo challenge
            </Button>
          </Box>
        )}

        {expired && (
          <Box>
            <Alert severity="warning" sx={{ borderRadius: 3, mb: 2 }}>
              Challenge expired
            </Alert>
            <Typography sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
              Start a new photo challenge when you are ready to submit evidence.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStart}
              sx={{ mt: 3, py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }}
            >
              Start a new photo challenge
            </Button>
          </Box>
        )}

        {step === "error" && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>
              {error}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStart}
              sx={{ py: 1.5, borderRadius: 3, bgcolor: "#6b21a8", fontWeight: 900 }}
            >
              Try Again
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CardChallengeModal;
